import { useEffect } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

import type { Order } from '~hooks/useOrders/models';
import { findHiredTruckServiceInServicesList } from '~pages/Sales/Orders/newOrderFormSchema';
import { useStores } from '~store';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: the derived service identity — the defaultOrder's own service on
 *           edit, the company's Hired Truck service on create. Effectively
 *           once per mount: neither source changes while the form is open
 *           (the drawer loads services before mounting the form).
 * Writes:   `service`.
 * Guards:   no-op until the derived service has an id (e.g. a company with
 *           no Hired Truck service leaves the seeded null in place).
 * Legacy:   the legacy form's service derivation and sync effect.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("service sync").
 */
export function useServiceSync({
  defaultOrder,
  setValue,
}: {
  defaultOrder: Order | null;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
}): void {
  const { companyAssetsStore } = useStores();

  const service = defaultOrder
    ? defaultOrder.service
    : findHiredTruckServiceInServicesList(companyAssetsStore.services);

  useEffect(() => {
    if (service?.id) {
      setValue('service', service, { shouldDirty: false });
    }
    // Legacy deps verbatim ([service?.id]) — the service object and setValue
    // are read via closure, exactly as legacy reads them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id]);
}
